import {
  ASK_PUNTT_CHAT_ATOM_IDS,
  ASK_PUNTT_NESTED_ONLY_CHAT_ATOM_IDS,
  type AskPunttAgentStep,
  type AskPunttChatAtom,
  askPunttChatAtomSchema,
} from "@mg/schemas/src/christo/askPuntt";

export const RENDER_COMPONENT_TOOL = "render_component";

const NESTED_ONLY = new Set<string>(ASK_PUNTT_NESTED_ONLY_CHAT_ATOM_IDS);

/** snake_case keys to camelCase, recursively. Values are left alone. */
function camelCaseKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(camelCaseKeys);
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, inner]) => [
        key.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase()),
        camelCaseKeys(inner),
      ]),
    );
  }
  return value;
}

function normalizeRenderComponentArgs(
  args: unknown,
): Record<string, unknown> | undefined {
  let value = args;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return undefined;
    }
  }
  return value != null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function isStandaloneChatAtom(atom: AskPunttChatAtom): boolean {
  return !NESTED_ONLY.has(atom.componentId);
}

function parseChatAtom(raw: {
  componentId: string;
  instanceId: string;
  payload: unknown;
}): AskPunttChatAtom | null {
  const parsed = askPunttChatAtomSchema.safeParse({
    componentId: raw.componentId,
    instanceId: raw.instanceId,
    payload: camelCaseKeys(raw.payload ?? {}),
  });
  if (!parsed.success) return null;
  return isStandaloneChatAtom(parsed.data) ? parsed.data : null;
}

/**
 * Parse a warhol `render_component` tool_call's args into a drawable chat atom,
 * or null when the id is unknown, nested-only, or the payload fails schema.
 */
export function chatAtomFromRenderComponentArgs(
  args: Record<string, unknown> | undefined,
): AskPunttChatAtom | null {
  const normalized = normalizeRenderComponentArgs(args);
  if (!normalized) return null;
  const componentId =
    (typeof normalized.component_id === "string"
      ? normalized.component_id
      : undefined) ??
    (typeof normalized.componentId === "string"
      ? normalized.componentId
      : undefined);
  if (!componentId) return null;
  const instanceId =
    (typeof normalized.instance_id === "string" && normalized.instance_id.trim()
      ? normalized.instance_id
      : undefined) ??
    (typeof normalized.instanceId === "string" && normalized.instanceId.trim()
      ? normalized.instanceId
      : undefined) ??
    componentId;
  return parseChatAtom({
    componentId,
    instanceId,
    payload: normalized.payload,
  });
}

function componentIdFromRenderContext(
  args: Record<string, unknown> | undefined,
  resultSummary?: string,
): string | null {
  const normalized = normalizeRenderComponentArgs(args);
  if (normalized) {
    const fromArgs = normalized.component_id ?? normalized.componentId;
    if (typeof fromArgs === "string" && fromArgs.trim()) {
      return fromArgs.trim();
    }
  }
  const trimmed = resultSummary?.trim();
  if (trimmed?.startsWith("{")) {
    try {
      const ack = JSON.parse(trimmed) as Record<string, unknown>;
      const fromAck = ack.component_id ?? ack.componentId;
      if (typeof fromAck === "string" && fromAck.trim()) {
        return fromAck.trim();
      }
    } catch {
      // Not a RenderAck JSON summary — leave for the generic chip.
    }
  }
  return null;
}

function isKnownStandaloneChatAtomId(componentId: string): boolean {
  return (
    (ASK_PUNTT_CHAT_ATOM_IDS as readonly string[]).includes(componentId) &&
    !NESTED_ONLY.has(componentId)
  );
}

function isSuccessfulRenderAck(summary: string | undefined): boolean {
  const trimmed = summary?.trim();
  if (!trimmed) return true;
  if (!trimmed.startsWith("{")) return true;
  try {
    const ack = JSON.parse(trimmed) as Record<string, unknown>;
    return ack.rendered !== false;
  } catch {
    return true;
  }
}

function normalizePersistedComponent(raw: unknown): AskPunttChatAtom | null {
  if (raw == null || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const camel = camelCaseKeys(row) as Record<string, unknown>;
  const componentId =
    (typeof camel.componentId === "string" ? camel.componentId : undefined) ??
    (typeof row.component_id === "string" ? row.component_id : undefined);
  if (!componentId) return null;
  const instanceId =
    (typeof camel.instanceId === "string" && camel.instanceId.trim()
      ? camel.instanceId
      : undefined) ??
    (typeof row.instance_id === "string" && row.instance_id.trim()
      ? row.instance_id
      : undefined) ??
    componentId;
  return parseChatAtom({
    componentId,
    instanceId,
    payload: camel.payload ?? row.payload,
  });
}

/** Standalone atoms parsed from `render_component` tool_call rows (streaming lag). */
export function chatAtomsFromAgentSteps(
  steps: AskPunttAgentStep[] | undefined,
): AskPunttChatAtom[] {
  const atoms: AskPunttChatAtom[] = [];
  const seen = new Set<string>();
  const list = steps ?? [];

  for (let i = 0; i < list.length; i++) {
    const step = list[i];
    if (step.type !== "tool_call" || step.tool !== RENDER_COMPONENT_TOOL) {
      continue;
    }
    const next = list[i + 1];
    const result =
      next?.type === "tool_result" && next.tool === RENDER_COMPONENT_TOOL
        ? next
        : undefined;
    const atom = chatAtomFromRenderComponentArgs(step.args);
    if (atom == null) continue;
    // why: warhol can refuse a call after the model typed it; don't draw from a pair whose ack says rendered:false.
    if (result && !isSuccessfulRenderAck(result.summary)) continue;
    if (seen.has(atom.instanceId)) continue;
    seen.add(atom.instanceId);
    atoms.push(atom);
  }
  return atoms;
}

/** Merge persisted components with atoms parsed from agentSteps; dedupe by instanceId. */
export function mergeChatAtoms(
  persisted: AskPunttChatAtom[] | undefined,
  fromSteps: AskPunttChatAtom[],
): AskPunttChatAtom[] {
  const merged: AskPunttChatAtom[] = [];
  const seen = new Set<string>();
  for (const raw of persisted ?? []) {
    const atom = normalizePersistedComponent(raw);
    if (atom == null || seen.has(atom.instanceId)) continue;
    seen.add(atom.instanceId);
    merged.push(atom);
  }
  for (const atom of fromSteps) {
    if (seen.has(atom.instanceId)) continue;
    seen.add(atom.instanceId);
    merged.push(atom);
  }
  return merged;
}

/** Atoms Carrie should draw for this assistant message. */
export function collectDrawableChatAtoms(
  persisted: AskPunttChatAtom[] | undefined,
  steps: AskPunttAgentStep[] | undefined,
): AskPunttChatAtom[] {
  return mergeChatAtoms(persisted, chatAtomsFromAgentSteps(steps));
}

/**
 * True when this render_component pair should not appear as an AgentSteps chip.
 * Hide when this call draws an atom, or when a sibling/persisted atom for the
 * same known standalone id already covers the turn (Jerry: hide the failed
 * first call once the accepted feed-memory-card exists).
 */
export function shouldHideRenderComponentCall(
  call: AskPunttAgentStep & { type: "tool_call" },
  result: (AskPunttAgentStep & { type: "tool_result" }) | undefined,
  drawableAtoms: AskPunttChatAtom[],
): boolean {
  if (call.tool !== RENDER_COMPONENT_TOOL) return false;
  const parsed = chatAtomFromRenderComponentArgs(call.args);
  if (parsed != null) {
    if (result == null || isSuccessfulRenderAck(result.summary)) return true;
    // why: warhol refused this parseable call (rendered:false); still hide it
    // once a sibling/persisted atom for the same id draws, or its own drawn
    // chip and a leftover refused chip would both show (Jerry).
    return drawableAtoms.some(
      (atom) => atom.componentId === parsed.componentId,
    );
  }
  const componentId = componentIdFromRenderContext(call.args, result?.summary);
  if (componentId == null || !isKnownStandaloneChatAtomId(componentId)) {
    return false;
  }
  // why: payload may stream after component_id — hide in-flight mapped calls so Carrie
  // never flashes raw JSON before the atom draws.
  if (result == null) return true;
  if (isSuccessfulRenderAck(result.summary)) return true;
  return drawableAtoms.some((atom) => atom.componentId === componentId);
}

/** Secondary chip detail for tool_call rows — never dump render_component JSON. */
export function toolCallChipSecondaryText(
  step: {
    tool?: string;
    description?: string;
    args?: Record<string, unknown>;
  },
  drawingLabel: string,
): string {
  const description = step.description?.trim();
  if (description) return description;
  if (step.tool === RENDER_COMPONENT_TOOL) return drawingLabel;
  return "";
}

export function shouldHideRenderComponentResult(
  result: AskPunttAgentStep & { type: "tool_result" },
  drawableAtoms: AskPunttChatAtom[],
): boolean {
  if (result.tool !== RENDER_COMPONENT_TOOL) return false;
  const componentId = componentIdFromRenderContext(undefined, result.summary);
  if (componentId == null || !isKnownStandaloneChatAtomId(componentId)) {
    return false;
  }
  return drawableAtoms.some((atom) => atom.componentId === componentId);
}

/** @deprecated Use shouldHideRenderComponentCall */
export function isMappedRenderComponentCall(
  step: AskPunttAgentStep & { type: "tool_call" },
): boolean {
  return shouldHideRenderComponentCall(step, undefined, []);
}
